import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["children", "onConversationChange", "overlayAnchor"];
import { useCallback, useEffect, useRef, useState } from 'react';
import { Flex } from 'foundations-components/components/Flex';
import CopilotChannelProvider from 'copilot-toolkit/channel/CopilotChannelProvider';
import { Overlay, SIDEBAR_INLINE_SIZE } from './Overlay';
import { AcademyAIAssistantChatContainer } from './AcademyAIAssistantChatContainer';
import { useAssistantLayout, useEffectiveAssistantMode } from '../hooks/useAssistantLayout';
import { useAcademyAssistantContextBridge } from '../hooks/useAcademyAssistantContextBridge';
import { useBreezeAssistantExclusivity } from '../hooks/useBreezeAssistantExclusivity';
import { useDrawerPersistence } from '../hooks/useDrawerPersistence';
import { OpenBreezeAssistantConfirmModal } from './OpenBreezeAssistantConfirmModal';
import { onAsk, onOpenChatList, onOpenConversation, onStartNew } from '../magpie/academyAssistantChannel';

/**
 * The turn the container should render. The container applies these through an
 * effect keyed on `seq`, so each new intent bumps `seq` to re-run the effect
 * (driving `manager.open`/`startNew`/`sendMessage`) without remounting.
 */
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
/**
 * Host-side channel wiring, mounted once and always (not gated on open) so
 * pre-open context still lands and any root's `ask`/`open`/`startNew` reaches
 * the manager.
 */
const useAssistantHost = onConversationChange => {
  // Mirror cross-instance video-context snapshots into this instance's store.
  useAcademyAssistantContextBridge();
  const [intent, setIntent] = useState(() => ({
    seq: 0,
    // Reopen the conversation from the last session (persisted in the layout
    // store). Read once at mount; a stale/deleted id self-heals to a new chat
    // via the container's open/NOT_FOUND handling.
    conversationId: useAssistantLayout.getState().lastConversationId
  }));
  const seqRef = useRef(0);
  const nextSeq = useCallback(() => {
    seqRef.current += 1;
    return seqRef.current;
  }, []);
  useEffect(() => {
    // A forced `mode` wins; otherwise open only when closed, so an intent never
    // downgrades fullscreen to sidebar.
    const applyOpen = mode => {
      const layout = useAssistantLayout.getState();
      if (mode) layout.setMode(mode);else if (layout.mode === 'closed') layout.open();
    };
    // Persist the conversation an intent lands on (null = fresh chat / history),
    // so a refresh or remount before a new conversation is created honors the
    // intent instead of reopening the previous chat.
    const persistConversationId = id => {
      useAssistantLayout.getState().setLastConversationId(id);
    };
    const unsubscribes = [onAsk((text, options) => {
      persistConversationId(null);
      setIntent({
        seq: nextSeq(),
        conversationId: null,
        initialPrompt: text,
        source: options.source
      });
      applyOpen(options.mode);
    }), onOpenChatList(options => {
      persistConversationId(null);
      setIntent({
        seq: nextSeq(),
        conversationId: null,
        openHistory: true
      });
      applyOpen(options.mode);
    }), onOpenConversation(conversationId => {
      persistConversationId(conversationId);
      setIntent({
        seq: nextSeq(),
        conversationId
      });
      applyOpen();
    }), onStartNew(() => {
      persistConversationId(null);
      setIntent({
        seq: nextSeq(),
        conversationId: null
      });
      applyOpen();
    })];
    return () => unsubscribes.forEach(unsubscribe => unsubscribe());
  }, [nextSeq]);

  // `initialPrompt`/`openHistory` are one-shots consumed when the intent applies;
  // drop them after so a close+reopen (which remounts the container and re-runs
  // the apply effect for the same `seq`) doesn't replay the send or re-pop
  // history. Keyed on `seq` so the clear can't retrigger itself.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIntent(prev => prev.initialPrompt === undefined && prev.openHistory === undefined ? prev : Object.assign({}, prev, {
      initialPrompt: undefined,
      openHistory: undefined
    }));
  }, [intent.seq]);
  const handleConversationChange = useCallback(conversationId => {
    // Track the live conversation so the next intent starts from current state.
    setIntent(prev => Object.assign({}, prev, {
      conversationId
    }));
    // Persist it so a refresh or new-root navigation reopens this same chat.
    useAssistantLayout.getState().setLastConversationId(conversationId);
    // Forward to the consumer (e.g. to keep the conversation id in the URL).
    onConversationChange === null || onConversationChange === void 0 || onConversationChange(conversationId);
  }, [onConversationChange]);
  return {
    intent,
    onConversationChange: handleConversationChange
  };
};
/**
 * Wraps a page as an Academy Assistant host: renders the assistant (sidebar or
 * fullscreen; fullscreen on mobile) driven by `useAssistantLayout`, and wires
 * the host side of the same-window channel so any root can control it.
 *
 * Mount ONCE per app (`<AcademyAssistantShell>{page}</AcademyAssistantShell>`);
 * it owns the single channel subscriber + context bridge, so a second instance
 * would double up on intents. Control it with `useAcademyAssistant()`.
 */
export const AcademyAssistantShell = _ref => {
  var _intent$source, _intent$openHistory;
  let {
      children,
      onConversationChange: onConversationChangeProp,
      overlayAnchor
    } = _ref,
    containerProps = _objectWithoutPropertiesLoose(_ref, _excluded);
  const mode = useEffectiveAssistantMode();
  const close = useAssistantLayout(state => state.close);
  const {
    intent,
    onConversationChange
  } = useAssistantHost(onConversationChangeProp);
  const isSidebar = mode === 'sidebar';
  // Onboarding styling is a mount-time prop; alias it so the overlay and the
  // container agree — the overlay uses it to take over the whole viewport above
  // the nav/header when fullscreen.
  const isOnboardingFlow = containerProps.isOnboardingFlow;
  const isDrawerOpen = useDrawerPersistence(state => state.open);
  const {
    isConfirmingBreezeSwitch,
    confirmBreezeSwitch,
    cancelBreezeSwitch
  } = useBreezeAssistantExclusivity({
    isAcademyOpen: mode !== 'closed',
    isFullscreen: mode === 'fullscreen',
    onBreezeOpen: close
  });

  // Fill the parent region and anchor the absolutely-positioned overlay. The
  // page keeps a stable tree position across mode changes, so toggling the
  // assistant never remounts it. Sidebar reserves the panel's width with an
  // in-flow spacer so the page reflows beside it; fullscreen overlays the page.
  return /*#__PURE__*/_jsxs(CopilotChannelProvider, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      position: "relative",
      blockSize: "100%",
      inlineSize: "100%",
      children: [/*#__PURE__*/_jsxs(Flex, {
        flexDirection: "row",
        blockSize: "100%",
        inlineSize: "100%",
        children: [/*#__PURE__*/_jsx(Flex, {
          flex: "0 0 auto",
          inlineSize: isSidebar ? SIDEBAR_INLINE_SIZE : '0px'
        }), /*#__PURE__*/_jsx(Flex, {
          flex: 1,
          minInlineSize: "0px",
          flexDirection: "column",
          children: children
        })]
      }), mode !== 'closed' && /*#__PURE__*/_jsx(Overlay, {
        mode: mode,
        anchor: overlayAnchor,
        isOnboardingFlow: isOnboardingFlow,
        hideGradient: isSidebar && isDrawerOpen,
        children: /*#__PURE__*/_jsx(AcademyAIAssistantChatContainer, Object.assign({}, containerProps, {
          intentSeq: intent.seq,
          onClose: close,
          conversationId: intent.conversationId,
          initialPrompt: intent.initialPrompt,
          onConversationChange: onConversationChange,
          isCompact: isSidebar
          // `source` is a per-turn override; a later plain `ask` (field
          // undefined) falls back to containerProps, so it doesn't stick.
          ,
          source: (_intent$source = intent.source) !== null && _intent$source !== void 0 ? _intent$source : containerProps.source,
          isOnboardingFlow: isOnboardingFlow,
          openHistoryOnMount: (_intent$openHistory = intent.openHistory) !== null && _intent$openHistory !== void 0 ? _intent$openHistory : containerProps.openHistoryOnMount
        }))
      })]
    }), isConfirmingBreezeSwitch && /*#__PURE__*/_jsx(OpenBreezeAssistantConfirmModal, {
      onConfirm: confirmBreezeSwitch,
      onCancel: cancelBreezeSwitch
    })]
  });
};